import React, { MutableRefObject, RefObject, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { useBoolean } from 'react-use';
import { twMerge } from 'tailwind-merge';

interface IProps {
    className?: string;
    children: React.ReactNode;
    clickOutsideToHide?: boolean;
    isShow: boolean;
    fromBottom?: boolean;
    onClose: () => void;
}

export function Modal({
    className,
    children,
    clickOutsideToHide,
    isShow,
    fromBottom,
    onClose,
}: IProps) {
    const modalRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const [animation, setAnimation] = useBoolean(false);

    const handleDoubleClick = (evt: React.MouseEvent) => {
        evt.preventDefault();
        evt.stopPropagation();
    };

    useEffect(() => {
        setTimeout(() => setAnimation(true), 50);
        return () => setAnimation(false);
    }, [isShow]);

    const handleClick = (evt: React.MouseEvent) => {
        evt.stopPropagation();
        if (!modalRef.current.contains(evt.target) && clickOutsideToHide) {
            onClose();
        }
    };

    return (
        <div
            onDoubleClick={handleDoubleClick}
            onClick={handleClick}
            className={twMerge(
                'modal',
                'relative',
                'max-h-full',
                'mx-[15px]',
                'rounded-[3px]',
                'bg-white',
                'flex-col justify-center',
                'hidden',
                className,
                classnames({
                    ['flex']: isShow,
                    ['is-bottom']: fromBottom,
                    ['show-animation']: animation && isShow,
                })
            )}
        >
            <div
                ref={modalRef}
                role="presentation"
                className={classnames('modal-background')}
            >
                <div
                    className={twMerge('modal-content', 'relative', classnames({
                        [classnames(
                            'absolute bottom-0 top-auto',
                            'w-full h-auto',
                            'bg-white',
                            'shadow-[0px_-2px_2px_rgba(0,0,0,0.15)]',
                            'translate-y-full will-change-transform',
                            'transition-transform duration-[0.2s] ease-[ease-in-out]'
                        )]: fromBottom,
                        ['translate-y-0']: animation && isShow && fromBottom,
                    }))}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

export default Modal;
